import classNames from "classnames/dedupe";
import { useCallback, useEffect, useRef, useState } from "react";
import { Content } from "./Content";
import { Details } from "./Details";
import { scrollToAssumptionGroup } from "./scrollToAssumptionGroup";
import type { CollapsibleProps } from "./types";
import "./Collapsible.css";

const Collapsible = ({ ref, ...props }: CollapsibleProps) => {
  const {
    assumptionGroups,
    className,
    onConfirmAssumptionGroup,
    onAllConfirmed,
    expandedAssumptionGroup,
    confirmedAssumptionGroups,
    onEditQuestion,
    onAssumptionGroupExpanded,
    ...rest
  } = props;
  const sentAllConfirmed = useRef(false);
  const [openAssumptionGroup, setOpenAssumptionGroup] = useState<number>(
    expandedAssumptionGroup ?? 0,
  );
  const [_confirmedAssumptionGroups, setConfirmedAssumptionGroups] = useState<
    Set<number>
  >(confirmedAssumptionGroups ?? new Set());
  const allAssumptionGroupsConfirmed = useCallback(() => {
    return assumptionGroups.length === _confirmedAssumptionGroups.size;
  }, [assumptionGroups, _confirmedAssumptionGroups]);

  useEffect(() => {
    if (
      allAssumptionGroupsConfirmed() &&
      onAllConfirmed &&
      !sentAllConfirmed.current
    ) {
      sentAllConfirmed.current = true;
      onAllConfirmed();
    }
  }, [allAssumptionGroupsConfirmed, confirmedAssumptionGroups, onAllConfirmed]);

  const handleExpandAssumptionGroup = (assumptionGroup: number) => {
    setOpenAssumptionGroup(assumptionGroup);
    onAssumptionGroupExpanded?.(assumptionGroup);
  };

  const handleConfirm = (assumptionGroup: number) => {
    onConfirmAssumptionGroup?.(assumptionGroup);
    if (document.startViewTransition) {
      document.startViewTransition?.(() => {
        setConfirmedAssumptionGroups(prev =>
          new Set(prev).add(assumptionGroup),
        );
        handleExpandAssumptionGroup(assumptionGroup + 1);
      });
    } else {
      setConfirmedAssumptionGroups(prev => new Set(prev).add(assumptionGroup));
      handleExpandAssumptionGroup(assumptionGroup + 1);
    }
  };

  useEffect(() => {
    if (openAssumptionGroup) {
      scrollToAssumptionGroup(openAssumptionGroup);
    }
  }, [openAssumptionGroup]);

  const handleReOpen = (assumptionGroup: number) => {
    handleExpandAssumptionGroup(assumptionGroup);
    scrollToAssumptionGroup(assumptionGroup);
  };

  const classes = classNames("mobius", "mobius-collapsible", className);

  return (
    <div ref={ref} className={classes} {...rest}>
      {assumptionGroups.map(({ header, content, help }, index) => (
        <Details
          id={`assumption-group-${index}`}
          key={header}
          header={header}
          onReOpen={handleReOpen}
          assumptionGroupIndex={index}
          isOpen={openAssumptionGroup === index}
          isConfirmed={_confirmedAssumptionGroups.has(index)}
          onConfirm={handleConfirm}
          helpText={help}
        >
          {content.map(question => (
            <Content
              key={question.name}
              data-assumption-group-index={index}
              assumptionGroup={header}
              onEditQuestion={() => onEditQuestion?.(question.name)}
              {...question}
            />
          ))}
        </Details>
      ))}
    </div>
  );
};

Collapsible.displayName = "Collapsible";
export { Collapsible };
